// GraphRow.tsx — the SVG pieces of one graph row: the branch-label pill and
// the commit "combo-button".
//
// A row is an AuthZ-style button — a rounded rect stroked in the branch's
// color — divided into segments:
//
//     <author-initial avatar> | abc1234 | <subject> | <metric=val>
//
// The headline metric is whichever of `preferred` the commit carries first;
// the caller orders that list so the experiment's position-0 objective leads
// it. A status pip on the outer edge colors green / yellow / red for
// improved / mixed / regressed metrics, and a selected row gains a
// body-colored ring — selection is drawn outside the button so picking a
// commit never reflows the graph. A headline value a dirty mark covers is
// struck through and labelled "dirty: <reason>" for assistive tech; the
// row's tooltip names the marks' reasons.
//
// Geometry constants and the text-width estimates come from graphLayout.ts;
// nothing here measures the DOM, because the graph is one SVG with a fixed
// viewBox and Fira Code is monospace.
import { colorForHue, hueOf, metricColor, preferWhiteFg } from './colors';
import { dirtySvgProps, dirtyTitle } from './dirty';
import { formatNum } from './format';
import type { ThemeMode } from './theme';
import { isAdditiveClick } from './viewState';
import type { SyntheticEvent } from 'react';
import type { CIJob, CIJobStatus, MetricStatus } from '../api';
import {
  AVATAR_R, BUTTON_GAP, BUTTON_H, BUTTON_PAD_X, BUTTON_RX, CHAR_W, DOT_R,
  JOB_DOT_GAP, JOB_DOT_R, LABEL_H, MAX_JOB_DOTS, PIP_R, SIDE_MARGIN, VIEWBOX_W,
  avatarInitial, jobSegWidth, pillWidth, toplineMetric,
  type Lane, type Row,
} from './graphLayout';

const PIP_COLOR: Record<MetricStatus, string> = {
  improved: 'var(--bs-success, #198754)',
  mixed: 'var(--bs-warning, #ffc107)',
  regressed: 'var(--bs-danger, #dc3545)',
};

// CI job dots: green running, yellow queued/starting, red failed.
const JOB_COLOR: Record<CIJobStatus, string> = {
  running: 'var(--bs-success, #198754)',
  pending: 'var(--bs-warning, #ffc107)',
  failed: 'var(--bs-danger, #dc3545)',
  succeeded: 'var(--bs-secondary-color, #888)',
};

const JOB_VERB: Record<CIJobStatus, string> = {
  running: 'running',
  pending: 'queued',
  failed: 'failed',
  succeeded: 'done',
};

export function jobLabel(j: CIJob): string {
  const what = j.target ? j.target.replace(/^.*:/, '') : j.name;
  const state = j.status === 'pending' && !j.suspended ? 'starting' : JOB_VERB[j.status];
  const tries = j.failed_attempts > 0 ? `, ${j.failed_attempts} failed attempt${j.failed_attempts === 1 ? '' : 's'}` : '';
  return `${what}: ${state}${tries}`;
}

export function BranchPill({ x, y, lane }: { x: number; y: number; lane: Lane }) {
  const label = lane.branch;
  const w = pillWidth(label);
  return (
    <g transform={`translate(${x - w / 2}, ${y})`}>
      {/* Outline-only — matches the commit-row combo-button chrome.
          Transparent fill lets the body surface show through; the lane color
          reads on both stroke and text since the palette solver targets
          minBgContrast against the body bg. */}
      <rect
        x={0}
        y={0}
        width={w}
        height={LABEL_H}
        rx={BUTTON_RX}
        fill="transparent"
        stroke={lane.color}
        strokeWidth={1.5}
      />
      <text
        x={w / 2}
        y={15}
        textAnchor="middle"
        fontSize={11}
        fontWeight={700}
        fill={lane.color}
      >
        {label}
      </text>
    </g>
  );
}

export function CommitRow({
  row,
  units,
  preferred,
  mode,
  selected,
  onPick,
  jobs = [],
  onCancelJob,
}: {
  row: Row;
  units: Record<string, string>;
  preferred: string[];
  mode: ThemeMode;
  selected: boolean;
  onPick: (additive: boolean) => void;
  /** CI jobs measuring this commit (queued, running or failed). */
  jobs?: CIJob[];
  /** Click on a job dot: cancel that job. Absent = dots are display-only. */
  onCancelJob?: (job: CIJob) => void;
}) {
  const { commit, lane, y, side } = row;
  const dotX = lane.x;

  const hash7 = commit.sha.slice(0, 7);
  // A commit increment only knows by sha (a value posted for an unpushed
  // commit) has no message; the row drops the subject segment rather than
  // repeating the hash beside itself.
  const subject = (commit.message || '').split('\n')[0].trim().slice(0, 20);
  const init = avatarInitial(commit.author_name || '', commit.author_email || '');

  // Author avatar + topline metric colors track the theme.
  const authorColor = colorForHue(
    hueOf(commit.author_email || commit.author_name || '?'),
    mode,
  );
  const topline = toplineMetric(commit.metrics, preferred);
  const toplineNumber = topline
    ? formatNum(topline[1]) + (units[topline[0]] || '')
    : '';
  const toplineColor = topline ? metricColor(topline[0], mode) : undefined;
  const toplineDirty = topline ? commit.dirty?.[topline[0]] : undefined;
  const toplineValue = toplineNumber;

  // Combo-button segments: [avatar][hash][subject][topline?].
  const avatarSegW = 2 * AVATAR_R + 8;
  const hashSegW = hash7.length * CHAR_W + 2 * BUTTON_PAD_X;
  const toplineSegW = topline
    ? toplineValue.length * CHAR_W + 2 * BUTTON_PAD_X
    : 0;
  const jobSegW = jobSegWidth(jobs.length);

  // Figure out how much horizontal room the subject segment has.
  const buttonStartX = side === 'right' ? dotX + DOT_R + BUTTON_GAP : dotX - DOT_R - BUTTON_GAP;
  const availPx = side === 'right'
    ? VIEWBOX_W - SIDE_MARGIN - buttonStartX - avatarSegW - hashSegW - toplineSegW - jobSegW
    : buttonStartX - SIDE_MARGIN - avatarSegW - hashSegW - toplineSegW - jobSegW;
  const subjectAvailPx = Math.max(80, availPx);
  const maxSubjectChars = Math.max(
    8,
    Math.floor((subjectAvailPx - 2 * BUTTON_PAD_X) / CHAR_W),
  );
  const subjectShort = subject.length <= maxSubjectChars
    ? subject
    : subject.slice(0, maxSubjectChars - 1) + '…';
  const subjectSegW = subjectShort
    ? subjectShort.length * CHAR_W + 2 * BUTTON_PAD_X
    : 0;

  const buttonW = avatarSegW + hashSegW + subjectSegW + toplineSegW + jobSegW;
  const buttonH = BUTTON_H;
  const buttonY = y - buttonH / 2;
  const buttonX = side === 'right' ? buttonStartX : buttonStartX - buttonW;

  // Segment x-boundaries (left edges).
  const avatarX0 = buttonX;
  const hashX0 = avatarX0 + avatarSegW;
  const subjectX0 = hashX0 + hashSegW;
  const toplineX0 = subjectX0 + subjectSegW;
  const avatarCX = avatarX0 + avatarSegW / 2;
  const hashCX = hashX0 + hashSegW / 2;
  const toplineCX = toplineX0 + toplineSegW / 2;
  const jobsX0 = toplineX0 + toplineSegW;

  const pipX = side === 'right' ? buttonX - PIP_R - 1 : buttonX + buttonW + PIP_R + 1;
  const status = commit.metric_status;
  const pipFill = status ? PIP_COLOR[status] : undefined;

  return (
    <g style={{ cursor: 'pointer' }} onClick={(e) => onPick(isAdditiveClick(e))}>
      <title>
        {hash7}
        {subject ? `\n${subject}` : ''}
        {commit.author_name ? `\n— ${commit.author_name}` : ''}
        {commit.metrics
          ? '\n' +
            Object.entries(commit.metrics)
              .map(([n, v]) => `${n}=${formatNum(v)}${units[n] || ''}${commit.dirty?.[n] ? ' (dirty)' : ''}`)
              .join(', ')
          : ''}
        {commit.dirty
          ? '\n' + dirtyTitle(Object.values(commit.dirty).flat()
            .filter((r, i, all) => all.findIndex((x) => x.id === r.id) === i))
          : ''}
        {status ? `\nstatus: ${status}` : ''}
      </title>
      {/* Lane dot — author-colored. */}
      <circle cx={dotX} cy={y} r={DOT_R} fill={authorColor} stroke="var(--bs-body-bg)" strokeWidth={2} />
      {/* Combo-button outer outline — branch color, transparent fill,
          slightly heavier stroke + a body-color ring when the commit is in
          the overview selection, so picked rows pop without changing
          layout. */}
      {selected && (
        <rect
          x={buttonX - 3}
          y={buttonY - 3}
          width={buttonW + 6}
          height={buttonH + 6}
          rx={BUTTON_RX + 3}
          fill="transparent"
          stroke="var(--bs-body-color)"
          strokeWidth={1.5}
          opacity={0.75}
        />
      )}
      <rect
        x={buttonX}
        y={buttonY}
        width={buttonW}
        height={buttonH}
        rx={BUTTON_RX}
        // Filled with the card surface so lane spines and fork connectors
        // that cross this row pass behind it instead of through the text.
        fill="var(--bs-card-bg, var(--bs-body-bg))"
        stroke={lane.color}
        strokeWidth={selected ? 2 : 1.5}
      />
      {/* Internal segment dividers (the "combo-button" look). */}
      <line x1={hashX0} y1={buttonY} x2={hashX0} y2={buttonY + buttonH} stroke={lane.color} strokeWidth={1} opacity={0.7} />
      {subjectShort && (
        <line x1={subjectX0} y1={buttonY} x2={subjectX0} y2={buttonY + buttonH} stroke={lane.color} strokeWidth={1} opacity={0.7} />
      )}
      {topline && (
        <line x1={toplineX0} y1={buttonY} x2={toplineX0} y2={buttonY + buttonH} stroke={lane.color} strokeWidth={1} opacity={0.7} />
      )}
      {/* Avatar segment. */}
      <circle cx={avatarCX} cy={y} r={AVATAR_R} fill={authorColor} />
      <text
        x={avatarCX}
        y={y + 4}
        textAnchor="middle"
        fontSize={12}
        fontWeight={700}
        fill={preferWhiteFg(authorColor) ? '#ffffff' : '#1a1a1a'}
      >
        {init}
      </text>
      {/* Hash segment. */}
      <text
        x={hashCX}
        y={y + 4}
        textAnchor="middle"
        fontSize={13}
        fontWeight={600}
        fill="var(--bs-secondary-color, var(--bs-body-color))"
      >
        {hash7}
      </text>
      {/* Subject segment. */}
      <text
        x={subjectX0 + BUTTON_PAD_X}
        y={y + 4}
        textAnchor="start"
        fontSize={13}
        fill="var(--bs-body-color)"
      >
        {subjectShort}
      </text>
      {/* Topline metric segment — color from the metric NAME. */}
      {topline && (
        <text
          x={toplineCX}
          y={y + 4}
          textAnchor="middle"
          fontSize={13}
          fontWeight={700}
          fill={toplineColor}
        >
          {toplineDirty ? (
            <tspan className="dirty-value" {...dirtySvgProps(toplineDirty)}>{toplineNumber}</tspan>
          ) : toplineValue}
        </text>
      )}
      {/* CI job segment: one dot per job; clicking a dot cancels it (the
          page confirms first). The click must not also select the row. */}
      {jobs.length > 0 && (
        <line x1={jobsX0} y1={buttonY} x2={jobsX0} y2={buttonY + buttonH} stroke={lane.color} strokeWidth={1} opacity={0.7} />
      )}
      {jobs.slice(0, MAX_JOB_DOTS).map((j, i) => {
        const cx = jobsX0 + BUTTON_PAD_X + JOB_DOT_R + i * (2 * JOB_DOT_R + JOB_DOT_GAP);
        const label = jobLabel(j) + (onCancelJob ? ` — click to ${j.status === 'failed' ? 'clear' : 'cancel'}` : '');
        const act = (e: SyntheticEvent) => {
          e.stopPropagation();
          onCancelJob?.(j);
        };
        return (
          <g
            key={j.name}
            className="ci-job-dot"
            role={onCancelJob ? 'button' : 'img'}
            tabIndex={onCancelJob ? 0 : undefined}
            aria-label={label}
            onClick={act}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') act(e); }}
          >
            <title>{label}</title>
            {/* A larger transparent hit target than the 5px dot. */}
            <circle cx={cx} cy={y} r={JOB_DOT_R + 4} fill="transparent" />
            <circle cx={cx} cy={y} r={JOB_DOT_R} fill={JOB_COLOR[j.status]} />
          </g>
        );
      })}
      {jobs.length > MAX_JOB_DOTS && (
        <text
          x={jobsX0 + BUTTON_PAD_X + MAX_JOB_DOTS * (2 * JOB_DOT_R + JOB_DOT_GAP)}
          y={y + 4}
          fontSize={12}
          fill="var(--bs-secondary-color, var(--bs-body-color))"
        >
          +{jobs.length - MAX_JOB_DOTS}
        </text>
      )}
      {pipFill && (
        <circle
          cx={pipX}
          cy={y}
          r={PIP_R}
          fill={pipFill}
          stroke="var(--bs-body-bg)"
          strokeWidth={2}
        />
      )}
    </g>
  );
}
